import ArtistCards from "@/components/ArtistCards";
import {
  Box,
  Center,
  Container,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useDisclosure,
} from "@chakra-ui/react";
import type { InferGetStaticPropsType } from "next";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { AiOutlineInfoCircle } from "react-icons/ai";

interface ArtistLink {
  title: string;
  link: string;
}

interface CardFile {
  file_name: string;
}

interface ArtistPageCards {
  id: number;
  issued: string;
  quantity: number;
  slug: string;
  inc_list: string[];
  sold_out: boolean;
  quantiy: number;
  includes: string;
  desc: string;
  comp_file?: string;
  uuid: string;
  price: number;
  thumbnail: string;
  title: string;
  image?: string;
  ext_file?: string;
  song: {
    id: number;
    file?: string;
    ext_file?: string;
    title: string;
  };
  files: CardFile[];
}
interface ArtistProps {
  name: string;
  thumbnail: string;
  profile_image: string;
  slug: string;
  about: string;
  links: ArtistLink[];
  cards: ArtistPageCards[];
  ArtistCards: JSX.Element;
}

export default function ArtistPage({
  artist,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { colorMode } = useColorMode();
  const backgroundColor = colorMode === "light" ? "white" : "black";
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <div>
      <Head>
        <title>{`${artist.name} is on Songcards`}</title>
        <link rel="icon" href="/SC Icon_A_Light.svg" />
        <link rel="apple-touch-icon" href="/SC Icon_A_Light.svg" />
        <meta name="description" content="Listen | Discover | Collect" />
        <link
          rel="canonical"
          href={`https://songcards.io/artists/${artist.slug}`}
        />
        <meta itemProp="name" content={`${artist.name} is on Songcards`} />
        <meta itemProp="description" content="Listen | Discover | Collect" />
        <meta itemProp="image" content={`${artist.profile_image ?? ""}`} />
        <meta property="og:title" content={`${artist.name} is on Songcards`} />
        <meta property="og:description" content="Listen | Discover | Collect" />
        <meta property="og:image" content={`${artist.profile_image ?? ""}`} />
        <meta
          property="og:url"
          content={`https://songcards.io/artists/${artist.slug}`}
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${artist.name} is on Songcards`} />
        <meta
          name="twitter:description"
          content="Listen | Discover | Collect"
        />
        <meta name="twitter:image" content={`${artist.profile_image ?? ""}`} />
      </Head>
      <Container mb={200} maxW="2xl">
        {artist.about && (
          <Center mb={5} mt={2}>
            <AiOutlineInfoCircle onClick={onOpen} size={22} />
          </Center>
        )}
        <Center>
          <Heading
            as="h1"
            maxW="sm"
            mt={2}
            mr={0}
            minW={100}
            mb={5}
            size="xl"
            textAlign="center"
          >
            {artist.name}
          </Heading>
        </Center>
        <Center>
          <Text mb={10}>is on Songcards</Text>
        </Center>
        <Modal isOpen={isOpen} onClose={onClose} motionPreset="slideInBottom">
          <ModalOverlay
            backdropInvert="80%"
            backdropFilter="blur(10px) hue-rotate(10deg)"
          />
          <ModalContent backgroundColor={backgroundColor} maxW={300}>
            <ModalHeader>About {artist.name}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={5}>{artist.about}</Text>
            </ModalBody>
          </ModalContent>
        </Modal>
        <Container mb={0}></Container>
        <ArtistCards cards={artist.cards} />
        <Box mt={10} mb={300}>
          <Link href={`/`}>
            <Center>
              <Image
                src={
                  colorMode === "dark"
                    ? "/SC Icon_B_White.svg"
                    : "/SC Icon_B_Black.svg"
                }
                width={30}
                height={500}
                alt="Discover more Songcards"
              />
            </Center>
          </Link>
        </Box>
      </Container>
      <Container mb={200}></Container>
    </div>
  );
}

interface ParamProps {
  params: {
    slug: string;
  };
}

export async function getStaticProps({ params }: ParamProps) {
  const { slug } = params;

  const res = await fetch(`https://api.songcards.io/artists/${slug}/`);

  const artist = await res.json();

  return {
    props: {
      artist,
    },
    revalidate: 86400,
  };
}

export async function getStaticPaths() {
  const endpoint = "https://api.songcards.io/artists/";

  if (endpoint) {
    const response = await fetch(endpoint);
    const artists = await response.json();

    const paths = await artists.map((artist: ArtistProps) => ({
      params: { slug: artist.slug },
    }));

    return { paths, fallback: "blocking" };
  }
}
